import type { CSSProperties, FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge, Button, Dialog, Switch, Textarea } from '@artosera/ui';
import { basculerModulePalier, basculerPalier, obtenirPaliers } from '../lib/api.js';
import { formaterDate } from '../lib/format.js';
import { useLibellesModules } from '../lib/modules.js';
import {
  actionGroupee,
  erreurPalier,
  estRevisionPerimee,
  etatPalier,
  paliersOrdonnes,
  pourcentage,
  type EtatPalier,
} from '../lib/paliers.js';
import { usePeut } from '../auth/usePeut.js';
import type { IdPalierDev, ModulePalierVue, PalierDevVue, PaliersVue } from '../types.js';
import pageStyles from '../styles/page.module.css';
import formStyles from '../styles/form.module.css';
import styles from './Paliers.module.css';

const MOTIF_MIN = 3;
const MOTIF_MAX = 500;
const CLE_PALIERS = ['console', 'paliers'] as const;

/** Ce que la boîte de confirmation s'apprête à envoyer : un palier entier, ou un module. */
type Cible =
  | { genre: 'palier'; palier: IdPalierDev; debloque: boolean }
  | { genre: 'module'; module: string; debloque: boolean };

/**
 * Paliers de développement : les investisseurs associés financent la plateforme
 * par paliers ; chacun débloque un lot de modules pour TOUTE la plateforme. La
 * direction de Venio (`console:kill-switch`, comme les interrupteurs globaux)
 * débloque ou verrouille un module, ou tout un palier. Les autres rôles de la
 * console lisent. Le palier est indépendant de l'urgence : un module débloqué
 * peut être coupé, et inversement — la page le rappelle sans le confondre.
 */
export function Paliers(): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const peut = usePeut();
  const ecriture = peut('console:kill-switch');
  const queryClient = useQueryClient();

  const [cible, setCible] = useState<Cible | null>(null);
  const [motif, setMotif] = useState('');
  const [erreurMotif, setErreurMotif] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  const requete = useQuery({ queryKey: CLE_PALIERS, queryFn: obtenirPaliers });

  const fermer = (): void => {
    setCible(null);
    setMotif('');
    setErreurMotif(null);
  };

  const nomPalier = (id: IdPalierDev): string => t(`paliers.palier.${id}.nom`);

  const mutation = useMutation({
    mutationFn: ({ cible: visee, motif: texte }: { cible: Cible; motif: string }) => {
      const revision = requete.data?.revision ?? 0;
      return visee.genre === 'palier'
        ? basculerPalier(visee.palier, visee.debloque, texte, revision)
        : basculerModulePalier(visee.module, visee.debloque, texte, revision);
    },
    onMutate: () => {
      setErreur(null);
      setSucces(null);
    },
    onSuccess: async (reponse: PaliersVue, { cible: visee }) => {
      queryClient.setQueryData(CLE_PALIERS, reponse);
      fermer();
      setSucces(
        visee.genre === 'palier'
          ? t(visee.debloque ? 'paliers.succesPalierDebloque' : 'paliers.succesPalierVerrouille', {
              palier: nomPalier(visee.palier),
            })
          : t(visee.debloque ? 'paliers.succesModuleDebloque' : 'paliers.succesModuleVerrouille', {
              module: libelles.libelle(visee.module),
            }),
      );
      // Les listes effectives de toutes les galeries et la page des interrupteurs changent.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['console', 'interrupteurs'] }),
        queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] }),
        queryClient.invalidateQueries({ queryKey: ['console', 'galerie'] }),
      ]);
    },
    onError: async (cause) => {
      fermer();
      const refus = erreurPalier(cause);
      setErreur(
        estRevisionPerimee(cause)
          ? t('paliers.conflit')
          : refus === 'introuvable'
            ? t('paliers.erreurIntrouvable')
            : refus !== null
              ? t('paliers.erreurNonVerrouillable')
              : t('paliers.erreurGenerique'),
      );
      // Dans tous les cas, on relit : l'écran ne garde pas un état que le serveur a démenti.
      await queryClient.invalidateQueries({ queryKey: CLE_PALIERS });
    },
  });

  const envoyer = (event: FormEvent): void => {
    event.preventDefault();
    if (!cible) return;
    const texte = motif.trim();
    if (texte.length < MOTIF_MIN) {
      setErreurMotif(t('paliers.erreurMotif'));
      return;
    }
    setErreurMotif(null);
    mutation.mutate({ cible, motif: texte });
  };

  const donnees = requete.data;
  const paliers = donnees ? paliersOrdonnes(donnees.paliers) : [];
  const synthese = paliers
    .filter((palier) => palier.verrouillable)
    .reduce(
      (cumul, palier) => ({
        debloques: cumul.debloques + palier.debloques,
        total: cumul.total + palier.total,
      }),
      { debloques: 0, total: 0 },
    );

  const palierCible = cible?.genre === 'palier' ? paliers.find((p) => p.id === cible.palier) : null;
  let titre = '';
  let texteDialogue = '';
  if (cible?.genre === 'palier') {
    const nom = nomPalier(cible.palier);
    titre = t(cible.debloque ? 'paliers.debloquerPalierTitre' : 'paliers.verrouillerPalierTitre', {
      palier: nom,
    });
    texteDialogue = t(
      cible.debloque ? 'paliers.debloquerPalierTexte' : 'paliers.verrouillerPalierTexte',
      { count: palierCible?.total ?? 0 },
    );
  } else if (cible) {
    const nom = libelles.libelle(cible.module);
    titre = t(cible.debloque ? 'paliers.debloquerModuleTitre' : 'paliers.verrouillerModuleTitre', {
      module: nom,
    });
    texteDialogue = t(
      cible.debloque ? 'paliers.debloquerModuleTexte' : 'paliers.verrouillerModuleTexte',
    );
  }

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('paliers.titre')}</h1>
          <p>{t('paliers.texte')}</p>
        </div>
      </div>

      {!ecriture ? (
        <p className={`${pageStyles.banniere} ${styles.lectureSeule}`}>
          {t('paliers.lectureSeule')}
        </p>
      ) : null}

      {/* Annonce la confirmation du serveur ; l'alerte ne sert qu'aux échecs. */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {succes ? (
          <p className={`${pageStyles.banniere} ${styles.succes}`}>
            <span className={styles.coche} aria-hidden="true">
              <IconeCoche />
            </span>
            {succes}
          </p>
        ) : null}
      </div>
      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      {requete.isLoading ? (
        <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>
      ) : requete.isError || !donnees ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {t('paliers.erreurChargement')}
        </p>
      ) : (
        <>
          <div className={styles.rappelIndependance}>
            <p>{t('paliers.distinct')}</p>
            {peut('console:kill-switch') ? (
              <Link to="/interrupteurs" className={`${styles.lien} ${styles.lienBloc}`}>
                {t('paliers.distinctLien')}
              </Link>
            ) : null}
            <p className={styles.synthese}>
              {t('paliers.synthese', { debloques: synthese.debloques, total: synthese.total })}
            </p>
          </div>

          <ol className={styles.feuilleDeRoute}>
            {paliers.map((palier) => (
              <li key={palier.id} className={styles.etape}>
                <BlocPalier
                  palier={palier}
                  ecriture={ecriture}
                  enCours={mutation.isPending}
                  cibleEnCours={mutation.isPending ? (mutation.variables?.cible ?? null) : null}
                  onPalier={(debloque) =>
                    setCible({ genre: 'palier', palier: palier.id, debloque })
                  }
                  onModule={(module, debloque) => setCible({ genre: 'module', module, debloque })}
                />
              </li>
            ))}
          </ol>
        </>
      )}

      <Dialog
        open={cible !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermer();
        }}
        title={titre}
        description={texteDialogue}
        closeLabel={t('paliers.fermer')}
      >
        <form onSubmit={envoyer} noValidate>
          <div className={formStyles.champ}>
            <label htmlFor="palier-motif">{t('paliers.champMotif')}</label>
            <Textarea
              id="palier-motif"
              rows={3}
              maxLength={MOTIF_MAX}
              required
              value={motif}
              aria-invalid={erreurMotif ? true : undefined}
              aria-describedby={
                erreurMotif ? 'palier-motif-aide palier-motif-erreur' : 'palier-motif-aide'
              }
              onChange={(event) => setMotif(event.target.value)}
            />
            <p id="palier-motif-aide" className={formStyles.aide}>
              {t('paliers.motifAide')}
            </p>
            {erreurMotif ? (
              <p id="palier-motif-erreur" role="alert" className={formStyles.erreur}>
                {erreurMotif}
              </p>
            ) : null}
          </div>
          <div className={formStyles.actionsFormulaire}>
            <Button type="button" variant="secondary" onClick={fermer}>
              {t('paliers.annuler')}
            </Button>
            <Button type="submit" variant="primary" loading={mutation.isPending}>
              {mutation.isPending
                ? t('paliers.enCours')
                : cible?.debloque
                  ? t('paliers.confirmerDebloquer')
                  : t('paliers.confirmerVerrouiller')}
            </Button>
          </div>
        </form>
      </Dialog>
    </section>
  );
}

interface BlocProps {
  palier: PalierDevVue;
  ecriture: boolean;
  enCours: boolean;
  cibleEnCours: Cible | null;
  onPalier: (debloque: boolean) => void;
  onModule: (module: string, debloque: boolean) => void;
}

function BlocPalier({
  palier,
  ecriture,
  enCours,
  cibleEnCours,
  onPalier,
  onModule,
}: BlocProps): JSX.Element {
  const { t } = useTranslation('console');
  const libelles = useLibellesModules();
  const etat = etatPalier(palier);
  const part = pourcentage(palier);
  const nom = t(`paliers.palier.${palier.id}.nom`);
  const idTitre = `palier-${palier.id}`;
  const geste = actionGroupee(palier);
  const palierEnCours = cibleEnCours?.genre === 'palier' && cibleEnCours.palier === palier.id;

  return (
    <section
      className={styles.bloc}
      aria-labelledby={idTitre}
      data-palier={palier.id}
      data-etat={etat}
    >
      <header className={styles.enteteBloc}>
        <div
          className={styles.anneau}
          role="progressbar"
          aria-label={t(`paliers.palier.${palier.id}.nom`)}
          aria-valuemin={0}
          aria-valuemax={palier.total}
          aria-valuenow={palier.debloques}
          aria-valuetext={t('paliers.progressionAria', {
            debloques: palier.debloques,
            total: palier.total,
          })}
          style={{ '--part': part } as CSSProperties}
        >
          <span className={styles.anneauCentre} aria-hidden="true">
            <IconeEtat etat={etat} />
          </span>
        </div>

        <div className={styles.titres}>
          <h2 id={idTitre} className={styles.nomPalier}>
            {nom}
          </h2>
          <p className={styles.sousTitre}>{t(`paliers.palier.${palier.id}.sousTitre`)}</p>
          <p className={styles.progression}>
            <span className={styles.chiffres}>
              {palier.verrouillable
                ? t('paliers.progression', { debloques: palier.debloques, total: palier.total })
                : t('paliers.progressionInclus', { total: palier.total })}
            </span>
            <Badge tone={TONS_ETAT[etat]} data-etat-palier={etat}>
              {t(`paliers.etat.${etat}`)}
            </Badge>
          </p>
        </div>

        {palier.verrouillable && ecriture ? (
          <div
            className={styles.actionsPalier}
            role="group"
            aria-label={t('paliers.groupeAction', { palier: nom })}
          >
            <Button
              variant="secondary"
              loading={palierEnCours}
              disabled={enCours}
              onClick={() => onPalier(geste === 'debloquer')}
            >
              {geste === 'debloquer' ? t('paliers.debloquerTout') : t('paliers.verrouillerTout')}
            </Button>
          </div>
        ) : null}
      </header>

      {palier.verrouillable ? (
        <ul className={styles.modules} aria-label={t('paliers.listeModules', { palier: nom })}>
          {palier.modules.map((module) => (
            <CarteModule
              key={module.id}
              module={module}
              lecture={!ecriture}
              bloque={enCours}
              enAttente={
                palierEnCours ||
                (cibleEnCours?.genre === 'module' && cibleEnCours.module === module.id)
              }
              onBascule={(debloque) => onModule(module.id, debloque)}
            />
          ))}
        </ul>
      ) : (
        <>
          <p className={styles.texteInclus}>{t('paliers.inclusTexte')}</p>
          <ul className={styles.coeur} aria-label={t('paliers.listeModules', { palier: nom })}>
            {palier.modules.map((module) => (
              <li key={module.id} className={styles.puceCoeur} data-module={module.id}>
                <span className={styles.cocheCoeur} aria-hidden="true">
                  <IconeCoche />
                </span>
                <span className={styles.nomCoeur}>{libelles.libelle(module.id)}</span>
                <span className={styles.inclus}>{t('paliers.inclusModule')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

const TONS_ETAT: Record<EtatPalier, 'available' | 'reserved' | 'withdrawn' | 'accent'> = {
  inclus: 'accent',
  debloque: 'available',
  partiel: 'reserved',
  verrouille: 'withdrawn',
};

interface CarteProps {
  module: ModulePalierVue;
  lecture: boolean;
  bloque: boolean;
  enAttente: boolean;
  onBascule: (debloque: boolean) => void;
}

function CarteModule({ module, lecture, bloque, enAttente, onBascule }: CarteProps): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const libelles = useLibellesModules();
  const urgence = module.coupeSecours ? 'secours' : module.coupeUrgence ? 'coupe' : null;
  const descriptionId = `palier-module-${module.id}-description`;

  return (
    <li
      className={styles.module}
      data-module={module.id}
      data-debloque={module.debloque ? 'oui' : 'non'}
    >
      <div className={styles.enteteModule}>
        <Switch
          id={`palier-module-${module.id}`}
          label={libelles.libelle(module.id)}
          checked={module.debloque}
          disabled={lecture || bloque}
          pending={enAttente}
          onCheckedChange={onBascule}
          aria-describedby={descriptionId}
        />
        <span className={styles.pastilles}>
          <Badge tone={module.debloque ? 'available' : 'withdrawn'}>
            {module.debloque ? t('paliers.moduleDebloque') : t('paliers.moduleVerrouille')}
          </Badge>
        </span>
      </div>
      <div id={descriptionId} className={styles.descriptionModule}>
        <p>{libelles.description(module.id)}</p>
        {!module.livre ? <p>{t('paliers.bientotTexte')}</p> : null}
        {module.livre && module.debloque && urgence === null && !module.disponible ? (
          <p>{t('paliers.dependanceFermee')}</p>
        ) : null}
        {urgence !== null ? (
          <p className={styles.rappelUrgence} data-urgence={urgence}>
            <span className={styles.pastilleUrgence} aria-hidden="true" />
            <span>
              {urgence === 'secours' ? t('paliers.coupeSecours') : t('paliers.coupeUrgence')}
              {peut('console:kill-switch') ? (
                <>
                  {' · '}
                  <Link to="/interrupteurs" className={styles.lien}>
                    {t('paliers.urgenceLien')}
                  </Link>
                </>
              ) : null}
            </span>
          </p>
        ) : null}
        {module.modifiePar && module.modifieLe ? (
          <p className={styles.trace}>
            {t('paliers.detailModif', {
              auteur: module.modifiePar,
              date: formaterDate(module.modifieLe, '—'),
              motif: module.motif ?? '—',
            })}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function IconeEtat({ etat }: { etat: EtatPalier }): JSX.Element {
  // Le trait seul dit l'état : coche (inclus, débloqué), tiret (partiel), cadenas (verrouillé).
  const trace =
    etat === 'verrouille'
      ? 'M5 7.5V6a3 3 0 016 0v1.5M4 7.5h8v5.5H4z'
      : etat === 'partiel'
        ? 'M4 8h8'
        : 'M3.5 8.5l3 3 6-6.5';
  return (
    <svg
      viewBox="0 0 16 16"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      data-icone-etat={etat}
    >
      <path d={trace} />
    </svg>
  );
}

function IconeCoche(): JSX.Element {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 8.5l3.2 3.2L13 4.8" />
    </svg>
  );
}
